import type { MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router';
import { Box } from '@mui/material';

export interface LibraryCardOpenTargetProps {
  /** Real destination; omit it for a card with no page to open and the target becomes a button. */
  to?: string;
  onOpen: () => void;
  /** Accessible name of the full-card target. */
  label: string;
  /** Id of the meta line read together with the name. */
  describedById: string;
  testId?: string;
}

/**
 * The card's single full-card open target — an invisible link (native `href`, so middle-click and
 * "open in new tab" keep working) or, without a `to`, an invisible button running `onOpen`.
 * It sits first in DOM/tab order, below the hover controls (zIndex 1 < 2) that stay independently clickable.
 */
export function LibraryCardOpenTarget({ to, onOpen, label, describedById, testId }: LibraryCardOpenTargetProps) {
  const handleOpen = (event: MouseEvent<HTMLAnchorElement>) => {
    // A modified or non-primary click (cmd/ctrl/shift/alt+click, middle-click, right-click) is
    // the user's own "open in new tab" / context-menu intent — let the browser's native anchor
    // behaviour handle it. Only a plain left-click is intercepted so `onOpen` can own routing.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onOpen();
  };

  if (to != null) {
    return (
      <Box
        component={RouterLink}
        to={to}
        onClick={handleOpen}
        aria-label={label}
        aria-describedby={describedById}
        data-testid={testId}
        sx={{ position: 'absolute', inset: 0, zIndex: 1 }}
      />
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={label}
      aria-describedby={describedById}
      data-testid={testId}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        p: 0,
        m: 0,
        border: 0,
        background: 'transparent',
        cursor: 'pointer'
      }}
    />
  );
}
